import { usePage } from '@inertiajs/vue3'
import { defineAsyncComponent } from 'vue'

type ModalCallback = (data?: unknown) => void

const defaultCallback: ModalCallback = () => {}

interface ModalData {
  modalOpened: boolean
  localModal: string | false
  modalData: Record<string, unknown>
  modalPersistentData: Record<string, unknown>
  modalCallback: ModalCallback
}

interface StackedModalData {
  modalOpened: boolean
  modal2Opened: boolean
  localModal: string | false
  localModal2: string | false
  modalData: Record<string, unknown>
  modal2Data: Record<string, unknown>
  modalCallback: ModalCallback
  modal2Callback: ModalCallback
}

type ModalContext = ModalData & {
  modal: string | false
  asyncModal: unknown
}

type StackedModalContext = StackedModalData & {
  modal: string | false
  modal2: string | false
  asyncModal: unknown
  asyncModal2: unknown
}

const useModal = {
  data(): ModalData {
    return {
      modalOpened: false,
      localModal: false,
      modalData: {},
      modalPersistentData: {},
      modalCallback: defaultCallback,
    }
  },
  computed: {
    modal() {
      if (!usePage().props) {
        return false
      }
      return (this as unknown as ModalContext).localModal
    },
    asyncModal() {
      const modal = (this as unknown as ModalContext).modal
      return modal
        ? defineAsyncComponent(() => import(`../apps/dashboard/dialogs/${modal}.vue`))
        : false
    },
  },
  watch: {
    asyncModal(newVal: unknown): void {
      const self = this as unknown as ModalContext
      if (newVal) {
        self.modalOpened = true
      } else {
        self.modalOpened = false
      }
    },
  },
  methods: {
    closeModal(): void {
      const self = this as unknown as ModalContext
      if (self.asyncModal) {
        self.modalOpened = false
        self.localModal = false
        self.modalData = {}
        self.modalCallback = defaultCallback
      }
    },
    openLocalModal(
      modalName: string,
      modalData: Record<string, unknown> = {},
      callback: ModalCallback = defaultCallback,
    ): void {
      const self = this as unknown as ModalContext
      self.localModal = modalName
      self.modalData = modalData
      self.modalCallback = callback
    },
    setModalPersistentData(data: Record<string, unknown>): void {
      const self = this as unknown as ModalContext
      self.modalPersistentData = data
    },
    cleanModalPersistentData(): void {
      const self = this as unknown as ModalContext
      self.modalPersistentData = {}
    },
  },
}

const useStackedModals = {
  data(): StackedModalData {
    return {
      modalOpened: false,
      modal2Opened: false,
      localModal: false,
      localModal2: false,
      modalData: {},
      modal2Data: {},
      modalCallback: defaultCallback,
      modal2Callback: defaultCallback,
    }
  },
  computed: {
    modal() {
      if (!usePage().props) {
        return false
      }
      return (this as unknown as StackedModalContext).localModal
    },
    modal2() {
      if (!usePage().props) {
        return false
      }
      return (this as unknown as StackedModalContext).localModal2
    },
    asyncModal() {
      const modal = (this as unknown as StackedModalContext).modal
      return modal
        ? defineAsyncComponent(() => import(`../apps/dashboard/dialogs/${modal}.vue`))
        : false
    },
    asyncModal2() {
      const modal2 = (this as unknown as StackedModalContext).modal2
      return modal2
        ? defineAsyncComponent(() => import(`../apps/dashboard/dialogs/${modal2}.vue`))
        : false
    },
  },
  watch: {
    asyncModal(newVal: unknown): void {
      const self = this as unknown as StackedModalContext
      if (newVal) {
        self.modalOpened = true
      } else {
        self.modalOpened = false
      }
    },
    asyncModal2(newVal: unknown): void {
      const self = this as unknown as StackedModalContext
      if (newVal) {
        self.modal2Opened = true
      } else {
        self.modal2Opened = false
      }
    },
  },
  methods: {
    closeModal(): void {
      const self = this as unknown as StackedModalContext
      if (self.asyncModal2) {
        self.modal2Opened = false
        self.localModal2 = false
        self.modal2Data = {}
        self.modal2Callback = defaultCallback
      } else if (self.asyncModal) {
        self.modalOpened = false
        self.localModal = false
        self.modalData = {}
        self.modalCallback = defaultCallback
      }
    },
    openLocalModal(
      modalName: string,
      modalData: Record<string, unknown> = {},
      callback: ModalCallback = defaultCallback,
    ): void {
      const self = this as unknown as StackedModalContext
      if (self.localModal) {
        self.localModal2 = modalName
        self.modal2Data = modalData
        self.modal2Callback = callback
      } else {
        self.localModal = modalName
        self.modalData = modalData
        self.modalCallback = callback
      }
    },
    fireModalCallback(data?: unknown): void {
      const self = this as unknown as StackedModalContext
      if (self.asyncModal2) {
        self.modal2Callback(data)
      } else {
        self.modalCallback(data)
      }
    },
  },
}

export { useModal, useStackedModals }
export type { ModalCallback, ModalData, StackedModalData }
